'use client';

import { useEffect, useRef, type ReactNode } from 'react';

interface MadLibsSlotOption {
  id: string;
  label: string;
  icon?: ReactNode;
}

interface MadLibsSlotProps {
  value: string | null;
  placeholder: string;
  options: MadLibsSlotOption[];
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSelect: (id: string) => void;
  /** Render inline within a sentence (desktop) or full-width (mobile) */
  mobile?: boolean;
}

export function MadLibsSlot({
  value,
  placeholder,
  options,
  isOpen,
  onToggle,
  onClose,
  onSelect,
  mobile,
}: MadLibsSlotProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selected = value ? options.find(o => o.id === value) : null;

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Focus first option when panel opens
  useEffect(() => {
    if (isOpen && panelRef.current) {
      const first = panelRef.current.querySelector<HTMLElement>('[role="option"]');
      first?.focus();
    }
  }, [isOpen]);

  const handleKeyNav = (e: React.KeyboardEvent) => {
    if (!isOpen) return;
    const items = panelRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    if (!items?.length) return;

    const current = document.activeElement as HTMLElement;
    const idx = Array.from(items).indexOf(current);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[Math.min(idx + 1, items.length - 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx <= 0) {
        triggerRef.current?.focus();
        onClose();
      } else {
        items[idx - 1]?.focus();
      }
    }
  };

  if (mobile) {
    return (
      <div className="relative">
        <button
          ref={triggerRef}
          onClick={onToggle}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className={`flex min-h-[44px] w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${
            selected
              ? 'border-brand/30 bg-brand/5 font-medium text-text-primary'
              : 'border-border-subtle bg-surface-secondary text-text-dim'
          }`}
        >
          <span className="flex items-center gap-2">
            {selected?.icon}
            {selected?.label || placeholder}
          </span>
          <svg
            width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          >
            <path d="M3 4.5 L6 7.5 L9 4.5" />
          </svg>
        </button>

        {isOpen && (
          <div
            ref={panelRef}
            role="listbox"
            onKeyDown={handleKeyNav}
            className="mt-1 max-h-48 overflow-y-auto rounded-xl border border-border-subtle bg-surface-elevated shadow-lg"
          >
            {options.map(opt => (
              <button
                key={opt.id}
                role="option"
                aria-selected={opt.id === value}
                onClick={() => { onSelect(opt.id); onClose(); }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover focus:bg-surface-hover focus:outline-none ${
                  opt.id === value ? 'bg-surface-hover font-medium text-text-primary' : 'text-text-secondary'
                }`}
              >
                {opt.icon && <span className="text-base">{opt.icon}</span>}
                <span className="truncate">{opt.label}</span>
                {opt.id === value && (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="ml-auto shrink-0 text-brand">
                    <path d="M3 7.5 L5.5 10 L11 4" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <span className="relative inline-block">
      <button
        ref={triggerRef}
        onClick={onToggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`inline-flex items-center gap-0.5 border-b-2 px-0.5 pb-px transition-colors ${
          selected
            ? 'border-brand/60 font-medium text-brand hover:border-brand'
            : 'border-dashed border-text-dim/40 text-text-dim hover:border-text-secondary hover:text-text-secondary'
        } ${isOpen ? 'border-brand bg-brand/5 text-brand' : ''}`}
      >
        <span className="flex items-center gap-1">
          {selected?.icon}
          {selected?.label || placeholder}
        </span>
        <svg
          width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
          className={`ml-0.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path d="M2.5 4 L5 6.5 L7.5 4" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={onClose} />
          <div
            ref={panelRef}
            role="listbox"
            onKeyDown={handleKeyNav}
            className="absolute left-0 top-full z-50 mt-1.5 max-h-64 min-w-[200px] overflow-y-auto rounded-xl border border-border-subtle bg-surface-elevated shadow-lg"
          >
            {options.map(opt => (
              <button
                key={opt.id}
                role="option"
                aria-selected={opt.id === value}
                onClick={() => { onSelect(opt.id); onClose(); }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover focus:bg-surface-hover focus:outline-none ${
                  opt.id === value ? 'bg-surface-hover font-medium text-text-primary' : 'text-text-secondary'
                }`}
              >
                {opt.icon && <span className="text-base">{opt.icon}</span>}
                <span className="truncate">{opt.label}</span>
                {opt.id === value && (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="ml-auto shrink-0 text-brand">
                    <path d="M3 7.5 L5.5 10 L11 4" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}
